import addDays from 'date-fns/addDays';
import max from 'date-fns/max';
import min from 'date-fns/min';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';

export function calculateStartDateConstraints(
  verticalMin: string | undefined,
  verticalMax: string | undefined,
  planMin: Date | undefined,
  planMaxExclusive: Date | undefined,
  now: Date = new Date(),
) {
  const verticalMinDate = verticalMin ? parseISO(verticalMin) : undefined;
  const verticalMaxExclusive = verticalMax ? addDays(parseISO(verticalMax), 1) : undefined;

  const earliestStartDate = verticalMinDate && planMin ? max([verticalMinDate, planMin]) : verticalMinDate ?? planMin;
  const latestStartDateExclusive =
    verticalMaxExclusive && planMaxExclusive
      ? min([verticalMaxExclusive, planMaxExclusive])
      : verticalMaxExclusive ?? planMaxExclusive;

  const firstAvailableInstant = earliestStartDate ? max([now, earliestStartDate]) : now;
  const hasAvailableStartDate = !latestStartDateExclusive || firstAvailableInstant < latestStartDateExclusive;

  return {
    earliestStartDate,
    latestStartDateExclusive,
    initialStartDate: hasAvailableStartDate ? firstAvailableInstant : undefined,
  };
}

export function isFutureStartDate(dateString: string | null): boolean {
  return Boolean(dateString && new Date(dateString) > new Date());
}

export function doesLocalDateOverlapInterval(
  date: Date,
  earliestStartDate: Date | undefined,
  latestStartDateExclusive: Date | undefined,
): boolean {
  const localDayStart = startOfDay(date);
  const localDayEnd = addDays(localDayStart, 1);

  if (earliestStartDate && localDayEnd <= earliestStartDate) {
    return false;
  }

  if (latestStartDateExclusive && localDayStart >= latestStartDateExclusive) {
    return false;
  }

  return true;
}

export function clampToEarliestStartDate(date: Date, earliestStartDate: Date | undefined): Date {
  const localMidnight = startOfDay(date);
  return earliestStartDate && localMidnight < earliestStartDate ? earliestStartDate : localMidnight;
}

export function hasSingleAvailableStartDate(
  initialStartDate: Date | undefined,
  latestStartDateExclusive: Date | undefined,
): boolean {
  if (!initialStartDate || !latestStartDateExclusive) {
    return false;
  }

  const firstAvailableDayEnd = addDays(startOfDay(initialStartDate), 1);
  const hasAvailableStartDate = initialStartDate < latestStartDateExclusive;

  return hasAvailableStartDate && latestStartDateExclusive <= firstAvailableDayEnd;
}

export function calculateSingleAvailableStartDate(
  verticalMinDate: string | undefined,
  verticalMaxDate: string | undefined,
  planMin: Date | undefined,
  planMaxExclusive: Date | undefined,
  now: Date = new Date(),
): Date | undefined {
  const { initialStartDate, latestStartDateExclusive, earliestStartDate } = calculateStartDateConstraints(
    verticalMinDate,
    verticalMaxDate,
    planMin,
    planMaxExclusive,
    now,
  );

  if (!initialStartDate || !hasSingleAvailableStartDate(initialStartDate, latestStartDateExclusive)) {
    return undefined;
  }

  return clampToEarliestStartDate(initialStartDate, earliestStartDate);
}
